import { WidgetOptions } from './types'
import { getCustomizations } from './customizations/customizations'
import { getWidgetCustomizations } from './customizations/widget-customizations'
import { getMainElement } from './render/render'
import { WidgetCustomizations } from './customizations/types'
import { getDummyProduct } from './models/dummy-product'
import { ensureWidgetInEditorMode, getProductInfo } from './services/shopify'
import { Product } from './models/product'
import { renderProduct, replacePrices } from './render/components/product'
import { fireWidgetReadyHook, sellingPlanChangedHookInit } from './services/hooks'
import { Publisher } from './services/event-bus/event-bus'
import { ProductPriceChangedEventArgs, subscribeToProductPriceChangedEvent } from './events/product-price-changed'
import { SellingPlanChangedEventArgs, subscribeToSellingPlanChangedEvent } from './events/selling-plan-changed'
import { setProduct, setProductPrice } from './store/product/actions'
import { setCustomizations } from './store/customizations/actions'
import { renderLivePreview } from './render/components/live-preview'
import { setMerchantAttributes } from './store/merchant/actions'
import { registerToVariantChange } from './services/variants/variants'
import { getProduct } from './store/product/getters'
import { injectWidget } from './services/injection/injection'
import { dispatchLoadedEvent, getLoadedEvent, initAddToCartAnalytics } from './services/analytics/events'

export class SubscriptionsAddToCartWidget {
  private element: HTMLElement
  private customizations: WidgetCustomizations
  private guid: string
  private instanceId: number
  private instanceVersionId: number

  init (widgetOptions: WidgetOptions) {
    this.element = widgetOptions.element
    this.guid = widgetOptions.merchantData.guid
    this.instanceId = widgetOptions.metadata.instanceId
    this.instanceVersionId = widgetOptions.metadata.instanceVersionId
    const overridenCustomizations = getCustomizations(widgetOptions.metadata.customizations, this.element)
    this.customizations = getWidgetCustomizations(overridenCustomizations)
    setCustomizations(this.customizations)
    setMerchantAttributes(this.guid)
  }

  run () {
    if (!this.shouldRenderWidget()) {
      return
    }
    const mainElement = getMainElement()
    injectWidget(this.element, mainElement)
    const loadedEvent = getLoadedEvent(this.instanceId, this.instanceVersionId)

    this.getProduct().then((product: Product) => {
      setProduct(product)
      renderProduct(mainElement)
      if (product.sellingPlanGroups.length > 0) {
        this.initEventsAndHooks()
        dispatchLoadedEvent(loadedEvent)
      }
    })

    ensureWidgetInEditorMode()

    if (this.customizations.livePreview.show) {
      renderLivePreview(mainElement)
    }

    if (this.customizations.hooks.widgetReady) {
      fireWidgetReadyHook()
    }
  }

  private getProduct (): Promise<Product> {
    if (this.customizations.productHandle) {
      return getProductInfo(this.customizations.productHandle)
    }
    return new Promise((resolve) => {
      resolve(getDummyProduct())
    })
  }

  private initEventsAndHooks () {
    if (this.customizations.hooks.sellingPlanChanged) {
      sellingPlanChangedHookInit()
    }
    if (this.customizations.priceLabel.strategy === 'starting_and_actual') {
      subscribeToSellingPlanChangedEvent((publisher: Publisher, args: SellingPlanChangedEventArgs) => {
        replacePrices()
      })
    }
    subscribeToProductPriceChangedEvent((publisher: Publisher, args: ProductPriceChangedEventArgs) => {
      setProductPrice(args.price)
      replacePrices()
    })
    if (getProduct().priceVaries) {
      registerToVariantChange()
    }
    initAddToCartAnalytics()
  }

  private shouldRenderWidget () {
    const inOnPreview = this.customizations.isPreview || this.customizations.isReadOnly
    const isOnSite = this.customizations.productHandle && (this.customizations.isLaunched || this.customizations.livePreview?.show)
    return isOnSite || inOnPreview
  }
}
